<template>
  <v-menu
    v-model="showDateOfInsuranceStartDatepicker"
    :close-on-content-click="false"
    :nudge-right="nudgeRight"
    transition="scale-transition"
    offset-y
    min-width="290px"
  >
    <template #activator="{ on, attrs }">
      <validation-provider
        v-slot="{ failed }"
        :vid="`${stepId}_date-of-insurance-start`"
        name="Date of Insurance Start"
        rules="required"
      >
        <qz-text-field
          v-model="dateOfInsuranceLabel"
          :data-test="fieldTrackerId"
          :error-messages="failed ? ['Please select a cover start date.'] : []"
          append-icon="mdi-calendar-blank"
          readonly
          v-bind="attrs"
          :placeholder="getPlaceholderResponsiveText(placeholder, $vuetify.breakpoint.smAndDown)"
          @click:append="showDateOfInsuranceStartDatepicker = true;"
          v-on="on"
        />
      </validation-provider>
    </template>
    <date-picker-test
      v-if="isDatePickerTestShown"
      v-model="model"
      :min="dateOfInsuranceMin"
      :max="dateOfInsuranceMax"
      @input="showDateOfInsuranceStartDatepicker = false"
    />
    <v-date-picker
      v-else
      v-model="model"
      :min="dateOfInsuranceMin"
      :max="dateOfInsuranceMax"
      @input="showDateOfInsuranceStartDatepicker = false"
    />
  </v-menu>
</template>

<script>
import {
  inject,
  ref,
  computed,
} from '@vue/composition-api';
import {
  UtcDate,
  ExtDate,
  DATEPICKER_LABEL_FORMAT,
} from '@/helpers/date';
import DatePickerTest from '@/components/DatePickerTest.vue';

export default {
  name: 'date-picker-menu',
  components: {
    DatePickerTest,
  },
  props: {
    value: {
      type: [String, Date],
      default: null,
    },
    stepId: {
      type: String,
      required: true,
    },
    fieldTrackerId: {
      type: String,
      required: true,
    },
    errorMessage: {
      type: String,
      default: 'Please select a cover start date.',
    },
    placeholder: {
      type: String,
      default: 'Please select a cover start date.',
    },
    datepickerLabelFormat: {
      type: String,
      default: DATEPICKER_LABEL_FORMAT,
    },
    nudgeRight: {
      type: Number,
      default: 40,
    },
  },
  model: {
    prop: 'value',
    event: 'update:modelValue',
  },
  setup (props, { emit }) {
    const today = new ExtDate();
    const todayUtc = new UtcDate();

    // if GTM shift is ahead of UTC date - set min date to today
    const dateOfInsuranceMin = today.isAfter(todayUtc) ? today.format() : todayUtc.format();

    // MODEL
    const model = computed({
      get () {
        return props.value;
      },
      set (val) {
        emit('update:modelValue', val);
      },
    });

    // DATA
    const showDateOfInsuranceStartDatepicker = ref(false);

    // INJECTS
    const productName = inject('productName');
    const constants = inject('constants');
    const config = inject('config');
    const {
      isQuotezonePartner,
      getPlaceholderResponsiveText,
    } = inject('helpers');

    // COMPUTED
    const dateOfInsuranceLabel = computed(() => {
      if (['', null, undefined].includes(model.value)) return null;

      const dateOfInsuranceStart = new UtcDate(model.value);

      let label = dateOfInsuranceStart.format(props.datepickerLabelFormat);

      if (today.format() === dateOfInsuranceStart.format()) {
        label += ' (today)';
      }
      return label;
    });
    const isCNIPartner = computed(() => {
      return [constants.CNI_PARTNER, constants.CNI_PARTNER_ALTERNATIVE].includes(config.partner);
    });
    const isDatePickerTestShown = computed(() => {
      return ['car'].includes(productName) && (isQuotezonePartner(config.partner) || isCNIPartner.value);
    });

    return {
      today,
      dateOfInsuranceMin,
      dateOfInsuranceMax: todayUtc.addDays(constants.DATE_OF_INSURANCE_START_MAX_DAYS).format(),

      // INJECTS
      getPlaceholderResponsiveText,

      // MODEL
      model,

      // DATA
      showDateOfInsuranceStartDatepicker,

      // COMPUTED
      dateOfInsuranceLabel,
      isDatePickerTestShown,
    };
  },
};
</script>
